<script setup>
// The processing / saved pair that repeats across the Save a Seat flows.
// Figma: 4669:55435 + 4669:55980 (Standard Add), and the same two screens again
// for Save a Seat (4669:55436 / 4669:55763) and the tail of Save a Seat when
// full (4669:56715 / 4669:56824).
//
// One component rather than six near-identical files: every one of those nodes
// is the same 901px pink panel with a 30px/1.3 centred headline — only the copy
// and whether it is still working actually change.
//
// The `full` state is deliberately elsewhere (seat-full.vue): it carries a
// notice box and two competing actions, a different shape.
// composables/helpers/ is a nested directory and is NOT auto-imported — every
// other consumer (leave.vue, reservations.vue) imports it explicitly. Calling it
// bare threw a ReferenceError that aborted setup, which left every computed
// undefined while props still resolved: the headline rendered blank while the
// buttons looked fine.
import { useWaitingMessages } from '@/composables/helpers/useWaitingMessages';

const props = defineProps({
    // 'processing' | 'saved' | 'waitlisted'
    //
    // NOTE: 'waitlisted' has NO Figma node — section 4669:55318 predates
    // Bizzabo's waitlist support and every screen there resolves to saved, full
    // or an error. Built to the same rules as its siblings (30px/1.3 headline,
    // the shared notice box, 45px rhythm) so it sits right, but the copy and
    // whether it should offer anything beyond View My Schedule need Nick.
    state: {
        type: String,
        default: 'processing',
        validator: (v) => ['processing', 'saved', 'waitlisted'].includes(v),
    },
    sessionTitle: { type: String, default: '' },
    // The attendee pressed Join Waitlist on a full session, so the wait is for a
    // waitlist place rather than a seat. Only the CALLER can know this: Bizzabo
    // reports the outcome with 200 vs 202, and that lands after this screen is
    // already up (UNB-751).
    joiningWaitlist: { type: Boolean, default: false },
    // Adding to My Schedule now waits on Bizzabo too (every add registers, rules
    // 3 and 6), so this screen covers a plain add as well as a seat claim. The
    // processing copy then describes the add — the seat wording would be wrong
    // on an uncapped session, and premature on a limited one, since nothing has
    // been added yet when this opens.
    adding: { type: Boolean, default: false },
    // Standard Add says the session was added AND the seat is being saved in one
    // breath; the explicit Save a Seat button only talks about the seat.
    includeAdded: { type: Boolean, default: false },
});
const open = defineModel('open', { default: true });
const emit = defineEmits(['close', 'view-schedule']);

const { getRandomMessage } = useWaitingMessages();

const quoted = computed(() => `“${props.sessionTitle}”`);

const modalHeadline = computed(() => {
    if (props.state === 'waitlisted') {
        // "Waitlist", not "standby" (client, 2026-09-03), reversing Bri's earlier
        // ruling (Asana 1217801996890680). The explainer below still refers to the
        // on-site standby LINE on purpose — that is the physical queue at the
        // door, not this list, and the venue calls it standby.
        return `${quoted.value} was full, so we\u2019ve added you to the waitlist.`;
    }
    if (props.state === 'processing') {
        // Checked before `adding`: joining a full session's waitlist adds it to
        // the schedule too, so both are true and the waitlist is the thing that
        // was asked for.
        if (props.joiningWaitlist) return `Adding you to the waitlist for ${quoted.value}...`;
        if (props.adding) return `Adding ${quoted.value} to My Schedule...`;
        return props.includeAdded
            ? `${quoted.value} has been added to My Schedule. Automatically working on saving your seat...`
            : `Working on saving your seat for ${quoted.value}...`;
    }
    return props.includeAdded
        ? `Your seat has been saved for ${quoted.value} and it has been added to My Schedule.`
        : `Your seat has been saved for ${quoted.value}.`;
});

// The comp shows one of the project's existing rotating waiting messages
// ("Lining everything up behind the scenes..."), so this reuses that list
// rather than inventing copy. Picked once per open, not per tick — it is a
// reassurance, not a progress readout.
const waitingMessage = ref(getRandomMessage());

const handleViewSchedule = () => {
    emit('view-schedule');
    open.value = false;
};
</script>

<template>
    <!-- `title` rather than `headline`, and the column owned here: the shell
         renders headline / body / actions as separate blocks with its own
         spacing, which cannot produce the comp's uniform 45px between steps
         (Session Modal 4669:55435 is gap-45 throughout).
         `persistent` while the reservation is in flight — the comp gives the
         processing screen no close affordance at all, which matches: a closed
         tab loses the seat. -->
    <FragmentsModalShell
        v-model:open="open"
        :title="modalHeadline"
        :persistent="state === 'processing'"
        @close="emit('close')"
    >
        <div class="flex flex-col items-center gap-[45px] w-full">
            <h2 class="font-hubspot-serif font-normal text-[25px] sm:text-[30px] leading-[1.3] tracking-normal text-(--magenta) text-center m-0 w-full">
                {{ modalHeadline }}
            </h2>

            <template v-if="state === 'processing'">
                <!-- Loader + message, 23px apart (4669:55435 → 3557:26865). -->
                <div class="flex flex-col items-center gap-[23px] w-full">
                    <FragmentsLoading loading class="w-[59px] h-[55px]" />
                    <p class="font-hubspot-sans font-light text-[16px] leading-[26px] tracking-[0.32px] sm:text-[14px] sm:leading-[1.5] sm:tracking-[0.28px] text-(--magenta) text-center m-0 w-full">
                        {{ waitingMessage }}
                    </p>
                </div>

                <!-- A compact white pill, NOT the full-width notice box the other
                     modals use: 20/10 padding and hugging its text (3568:38118). -->
                <!-- items-start, not items-center: once the copy wraps (mobile) a
                     centred icon floats between the two lines. mt-[2.5px] optically
                     centres the 16px glyph on the 21px first line. -->
                <div class="inline-flex items-start gap-[14px] bg-white rounded-[10px] px-[20px] py-[10px]">
                    <svg class="shrink-0 mt-[5px] sm:mt-[2.5px] size-[16px] text-(--magenta)" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <circle cx="8" cy="8" r="8" fill="currentColor" />
                        <path d="M8 4V9" stroke="white" stroke-width="1.5" stroke-linecap="round" />
                        <circle cx="8" cy="11.6" r="0.9" fill="white" />
                    </svg>
                    <p class="font-hubspot-sans font-light text-[16px] leading-[26px] tracking-[0.32px] sm:text-[14px] sm:leading-[1.5] sm:tracking-[0.28px] text-(--magenta) text-left m-0">
                        Please do not close this window or this process might be cancelled
                    </p>
                </div>
            </template>

            <!-- Waitlist explainer, same notice as the other modals. -->
            <div v-else-if="state === 'waitlisted'" class="flex items-start gap-[14px] w-full text-left bg-white rounded-[10px] px-[28px] py-[25px]">
                <svg class="shrink-0 mt-[4px] size-[16px] text-(--magenta)" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <circle cx="8" cy="8" r="8" fill="currentColor" />
                    <path d="M8 4V9" stroke="white" stroke-width="1.5" stroke-linecap="round" />
                    <circle cx="8" cy="11.6" r="0.9" fill="white" />
                </svg>
                <p class="font-hubspot-sans font-light text-[15px] leading-[23px] tracking-[0.3px] text-(--magenta) m-0">
                    You&rsquo;ve been added to the waitlist. If a seat opens up, attendees on the
                    waitlist will move to seat saved in the order they joined the waitlist. If you
                    are added as &ldquo;seat saved&rdquo;, you&rsquo;ll receive an email. There will
                    also be an onsite standby line with limited seats available on a first-come,
                    first-served basis.
                </p>
            </div>

            <div v-if="state !== 'processing'" class="flex justify-center">
                <button
                    type="button"
                    data-test="seat-view-schedule"
                    class="rounded-[50px] border border-(--magenta) text-(--magenta) px-[20px] py-[10px]
                        font-hubspot-serif text-[17px] leading-[1.4] tracking-[0.34px] cursor-pointer
                        transition-colors duration-200 hover:bg-(--magenta) hover:text-white
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--magenta)"
                    @click="handleViewSchedule"
                >View My Schedule</button>
            </div>
        </div>

        <!-- Only the resolved screen offers a way out; the comp gives the
             processing screen no Close at all. -->
        <div v-if="state !== 'processing'" class="flex justify-center mt-[25px]">
            <button
                type="button"
                data-test="seat-status-close"
                class="bg-transparent border-none p-0 cursor-pointer
                    font-hubspot-sans font-light text-[14px] leading-[1.5] tracking-[0.28px]
                    text-(--darker-pink) underline decoration-dotted decoration-[8.5%] underline-offset-[3px]
                    transition-colors duration-200 hover:text-(--purple) hover:no-underline"
                @click="open = false"
            >Close</button>
        </div>
    </FragmentsModalShell>
</template>
